/**
 * Favourites — stored in this browser.
 *
 * Product handles only, so a saved item survives a variant being renamed or
 * removed, and the list page fetches live product data rather than trusting a
 * stale copy of price or availability.
 *
 * Storage is deliberately the only backing: no account needed, nothing personal
 * leaves the device. If this later moves to a customer metafield or a wishlist
 * app, the swap is `read`/`write` below — the markup and events stay.
 */
(() => {
  const KEY = 'an:favourites:v1';
  const EVENT = 'an-favourites:changed';

  const read = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(KEY) || '[]');
      return Array.isArray(raw) ? raw.filter((h) => typeof h === 'string') : [];
    } catch {
      return [];   // private browsing, blocked storage, corrupt value
    }
  };

  const write = (list) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(list.slice(0, 200)));
    } catch {
      /* storage unavailable: the page still works, the list just will not persist */
    }
    document.dispatchEvent(new CustomEvent(EVENT, { detail: { list } }));
  };

  const has = (handle) => read().includes(handle);

  function toggle(handle) {
    const list = read();
    const at = list.indexOf(handle);
    if (at >= 0) list.splice(at, 1);
    else list.unshift(handle);
    write(list);
    return at < 0;
  }

  /* ------------------------------------------------------------- buttons */

  function paintButton(btn) {
    const handle = btn.getAttribute('data-favourite');
    if (!handle) return;
    const on = has(handle);
    btn.setAttribute('aria-pressed', String(on));
    btn.classList.toggle('is-on', on);
    const label = on ? btn.dataset.labelOn : btn.dataset.labelOff;
    if (label) btn.setAttribute('aria-label', label);
  }

  const paintAll = () => document.querySelectorAll('[data-favourite]').forEach(paintButton);

  function paintCount() {
    const n = read().length;
    document.querySelectorAll('[data-favourites-count]').forEach((el) => {
      el.textContent = n > 99 ? '99+' : String(n);
      el.hidden = n === 0;
    });
  }

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-favourite]');
    if (!btn) return;
    event.preventDefault();
    toggle(btn.getAttribute('data-favourite'));
    paintButton(btn);
  });

  // Another tab changed the list.
  window.addEventListener('storage', (e) => {
    if (e.key === KEY) { paintAll(); paintCount(); renderList(); }
  });
  document.addEventListener(EVENT, () => { paintCount(); renderList(); });

  /* ---------------------------------------------------------- list page */

  async function renderList() {
    const host = document.querySelector('[data-favourites-list]');
    if (!host) return;
    const empty = document.querySelector('[data-favourites-empty]');
    const handles = read();

    if (!handles.length) {
      host.innerHTML = '';
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;

    // Each card comes from templates/product.card.liquid: the same snippet the
    // collection grid renders, with live price and availability.
    const cards = await Promise.all(
      handles.map(async (handle) => {
        const res = await fetch(`/products/${encodeURIComponent(handle)}?view=card`).catch(() => null);
        if (!res || !res.ok) return null;   // unpublished or deleted since it was saved
        return { handle, html: await res.text().catch(() => '') };
      }),
    );

    const live = cards.filter((c) => c && c.html.trim());
    // Drop anything the storefront no longer serves, so the list cannot rot.
    if (live.length !== handles.length) {
      const keep = live.map((c) => c.handle);
      write(handles.filter((h) => keep.includes(h)));
    }

    host.innerHTML = live.map((c) => c.html).join('');
    paintAll();
  }

  const start = () => { paintAll(); paintCount(); renderList(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  window.ANFavourites = { read, has, toggle, count: () => read().length };
})();
